//* Members View Toggle Controller
//*
//* Switches the member listings between the card list and the full-screen
//* map view. The map is a fixed full-viewport overlay, so switching only
//* toggles the overlay (plus a scroll lock) — the list stays in place under
//* it. The active view is mirrored into the URL (?view=map) with
//* replaceState, so it survives reloads and can be shared; the server
//* renders the initial state from the param.
//*
//* In map view the filter modal applies and clears in place: the markers,
//* count badge and URL refresh from the listing's json response without a
//* page (and so map) reload. The modal renders at the layout bottom
//* (content_for :layout_bottom), outside this controller's element, so its
//* form/clear hooks are bound by hand rather than through Stimulus
//* actions/targets.
import ApplicationController from "application_controller.js";

export default class extends ApplicationController {
  debug = false;

  static targets = ["map", "listButton", "mapButton", "count"];

  connect() {
    this.filterForm = document.getElementById("filter-form");
    this.filterClearLink = document.getElementById("filter-clear");

    if (this.filterForm) {
      this.boundApplyFilters = this.applyFilters.bind(this);
      this.filterForm.addEventListener("submit", this.boundApplyFilters);
    }

    if (this.filterClearLink) {
      this.boundClearFilters = this.clearFilters.bind(this);
      this.filterClearLink.addEventListener("click", this.boundClearFilters);
    }

    //* A server-rendered ?view=map page (or a Turbo-restored one) must start
    //* with the scroll lock and button states in place
    this.syncPage(this.mapActive);
  }

  disconnect() {
    this.unlockScroll();

    if (this.boundApplyFilters) this.filterForm.removeEventListener("submit", this.boundApplyFilters);
    if (this.boundClearFilters) this.filterClearLink.removeEventListener("click", this.boundClearFilters);
  }

  get mapActive() {
    return this.hasMapTarget && !this.mapTarget.classList.contains("d-none");
  }

  showMap() {
    if (!this.hasMapTarget) return;

    this.mapTarget.classList.remove("d-none");
    this.syncPage(true);
    this.updateUrl(true);
    //* Wakes the lazily-initialized members map (view_components/map, mode "members")
    window.dispatchEvent(new CustomEvent("members-map:activate"));
  }

  showList() {
    if (!this.hasMapTarget) return;

    //* Filtering in place only refreshed the map — the server-rendered card list
    //* under the overlay is stale, so returning to it needs a real visit
    if (this.filtersApplied) {
      const url = new URL(window.location);
      url.searchParams.delete("view");
      if (window.Turbo) window.Turbo.visit(url.toString());
      else window.location.assign(url.toString());
      return;
    }

    this.mapTarget.classList.add("d-none");
    this.syncPage(false);
    this.updateUrl(false);
  }

  //* Map view filters without a page (and so map) reload: serialize the filter
  //* form, refresh the markers/badge from the listing's json response, and mirror
  //* the params into the URL. List view keeps the plain GET submission.
  applyFilters(event) {
    if (!this.mapActive) return;

    event.preventDefault();
    this.submitFiltersInPlace(this.filterForm);
  }

  clearFilters(event) {
    if (!this.mapActive) return;

    event.preventDefault();
    if (!this.filterForm) return;

    this.filterForm.querySelectorAll("input[type='checkbox']").forEach((checkbox) => { checkbox.checked = false; });
    this.filterForm.querySelectorAll("input[type='text']").forEach((input) => { input.value = ""; });
    this.submitFiltersInPlace(this.filterForm);
  }

  async submitFiltersInPlace(form) {
    const params = new URLSearchParams(new FormData(form));
    const query = params.toString();
    history.replaceState(history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
    this.filtersApplied = true;

    const jsonParams = new URLSearchParams(params);
    ["view", "page", "limit"].forEach((key) => jsonParams.delete(key));
    const jsonQuery = jsonParams.toString();
    const url = `${window.location.pathname}.json${jsonQuery ? `?${jsonQuery}` : ""}`;

    let geojson;
    try {
      const response = await fetch(url, { headers: { "Accept": "application/json" } });
      geojson = await response.json();
    } catch {
      return;
    }

    if (geojson.summary) this.countTargets.forEach((badge) => { badge.innerText = geojson.summary; });
    //* Handled by the members map (view_components/map, mode "members")
    window.dispatchEvent(new CustomEvent("members-map:update", { detail: { geojson, url } }));

    //* Close by clicking the header's dismiss button, so Bootstrap's data API
    //* closes the modal whichever controller opened it
    document.querySelector("#filterModal [data-bs-dismiss='modal']")?.click();
  }

  //* Mirrors the current view onto everything that carries view state: the
  //* toggle buttons, the scroll lock, the filter form's hidden view field and
  //* the filter modal's clear link
  syncPage(mapActive) {
    this.listButtonTargets.forEach((button) => button.classList.toggle("active", !mapActive));
    this.mapButtonTargets.forEach((button) => button.classList.toggle("active", mapActive));
    this.filterForm?.querySelectorAll("input[name='view']").forEach((field) => { field.disabled = !mapActive; });

    if (this.filterClearLink) {
      const url = new URL(this.filterClearLink.href, window.location.origin);
      if (mapActive) url.searchParams.set("view", "map");
      else url.searchParams.delete("view");
      this.filterClearLink.href = url;
    }

    if (mapActive) this.lockScroll();
    else this.unlockScroll();
  }

  updateUrl(mapActive) {
    const url = new URL(window.location);
    if (mapActive) url.searchParams.set("view", "map");
    else url.searchParams.delete("view");
    history.replaceState(history.state, "", url);
  }

  lockScroll() {
    document.documentElement.classList.add("overflow-hidden");
    document.body.classList.add("overflow-hidden");
  }

  unlockScroll() {
    document.documentElement.classList.remove("overflow-hidden");
    document.body.classList.remove("overflow-hidden");
  }
}
